var CoreObject = require("enyo/CoreObject"),
    FenwickTree,
    kind       = require("enyo/kind");

require("enyo-ui");

/**
 * [FenwickTree]{@link enyo-ui.FenwickTree} is a data structure that can
 * efficiently update elements and calculate prefix sums in a list of numbers.
 *
 * Unlike arrays, this tree starts indexing values at `1`.
 *
 * @class enyo-ui.FenwickTree
 * @public
 */
module.exports = FenwickTree = kind(/** @lends enyo-ui.FenwickTree.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.FenwickTree",

    /**
     * @private
     */
    kind: CoreObject,

    /**
     * The default value of a value in the list.
     *
     * @type {number}
     * @public
     */
    defaultValue: null,

    /**
     * The maximum length of the list.
     *
     * @type {number}
     * @public
     */
    maxLength: null,

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.reset(this.defaultValue, this.maxLength);
        };
    }),

    // ...........................
    // Private

    /**
     * Ensure index is a positive integer. It must not be lower than 1 or higher
     * than `this.maxLength`.
     *
     * @param {number}  index  The index
     * @private
     */
    checkIndex: function(index) {
        if (!FenwickTree.isInteger(index)) {
            this.error("Invalid index", index);
        }
        else if (index < 1 || index > this.maxLength) {
            this.error("Index out of range", index);
        }
    },

    /**
     * Ensure value is a finite number equal or larger than 0.
     *
     * @param {number}  value  The value
     * @private
     */
    checkValue: function(value) {
        if (typeof value !== "number") {
            this.error("Invalid value", value);
        }
        else if (value < 0 || !isFinite(value)) {
            this.error("Value out of bounds", value);
        }
    },

    /**
     * Ensure the length of the list is a positive integer.
     *
     * @param {number}  length  The length
     * @private
     */
    checkLength: function(length) {
        if (!FenwickTree.isInteger(length)) {
            this.error("Invalid length", length);
        }
        else if (length < 0) {
            this.error("Length out of bounds", length);
        }
    },

    /**
     * Get the tree frequency.
     *
     * @param  {number}  index  The index
     * @return {number}  The tree frequency
     * @private
     */
    getTreeFrequency: function(index) {

        // Return frequency as stored in tree
        if (typeof this.tree[index] === "number") {
            return this.tree[index];
        }

        // Return computed frequency based on default value
        return this.defaultValue * (index & -index);
    },

    /**
     * Updates the tree frequency at the given index by the given delta.
     *
     * @param {number}  index    The index
     * @param {number}  delta  The delta
     * @private
     */
    updateTreeFrequency: function(index, delta) {
        while (index <= this.maxLength) {
            this.tree[index] = this.getTreeFrequency(index) + delta;
            index            += (index & -index);
        }
    },

    /**
     * Find the index with a sum closest to the provided value.
     *
     * @param  {number}    sum     The sum
     * @param  {Function}  before  A comparator method, deciding if we should
     *                             search left or right.
     * @return {number}    The index closest to the provided value
     * @private
     */
    find: function(sum, before) {
        var left  = 0,
            right = this.msb << 1,
            sumT  = sum,
            middle,
            sumM;

        while (right > left + 1) {
            middle = (left + right) >> 1;
            sumM   = this.getTreeFrequency(middle);

            if (middle <= this.maxLength && before(sumM, sumT)) {
                sumT -= sumM;
                left = middle;
            }
            else {
                right = middle;
            }
        }

        return left;
    },

    // ...........................
    // Static

    /**
     * @private
     */
    statics: /** @lends  enyo-ui.FenwickTree */ {

        /**
         * Polyfill Number.isInteger as needed.
         *
         * @param  {?}        value  The value
         * @return {boolean}  `true` if value is an integer, `false` otherwise
         * @method
         * @static
         * @private
         */
        isInteger: Number.isInteger || function(value) {
            return typeof value === "number" && isFinite(value) && Math.floor(value) === value;
        },

        /**
         * Find the most significant bit in the provided value.
         *
         * @param  {number}  value  The value
         * @return {number}  The most significant bit
         * @static
         * @private
         */
        mostSignificantBit: function(value) {
            var result = value;

            result |= result >> 1;
            result |= result >> 2;
            result |= result >> 4;
            result |= result >> 8;
            result |= result >> 16;
            result |= result >> 32;
            result -= result >> 1;

            return result;
        }
    },

    // ...........................
    // Public

    /**
     * Reset the tree to a clean starting position.
     *
     * @param {number=}  defaultValue  The default value
     * @param {number=}  maxLength     The maximum length
     * @public
     */
    reset: function(defaultValue, maxLength) {

        // Apply defaultValue
        if (defaultValue || defaultValue === 0) {
            this.checkValue(defaultValue);
            this.defaultValue = defaultValue;
        }

        // Apply maxLength
        if (maxLength || maxLength === 0) {
            this.checkLength(maxLength);
            this.maxLength = maxLength;
            this.msb       = FenwickTree.mostSignificantBit(maxLength);
        }

        // Fenwick tree, used to sum item values
        this.tree = [];

        // Stores values of individual items
        this.values = [];
    },

    /**
     * Retrieves the default value of an item.
     *
     * @return {number}  The default value
     * @public
     */
    getDefaultValue: function() {
        return this.defaultValue;
    },

    /**
     * Returns the maximum length of the list.
     *
     * @return {number}  The maximum length
     * @public
     */
    getMaxLength: function() {
        return this.maxLength;
    },

    /**
     * Gets the value of the item at the given index.
     *
     * @param  {number}   index  The index
     * @return {?number}  The value if set, `null` otherwise.
     * @public
     */
    getValue: function(index) {
        this.checkIndex(index);

        // Return frequency as stored in tree
        if (typeof this.values[index] === "number") {
            return this.values[index];
        }

        // Return computed frequency based on default frequency
        return null;
    },

    /**
     * Clears the stored value of the item at the given index.
     *
     * @param {number}  index  The index
     * @public
     */
    unsetValue: function(index) {
        var oldValue,
            newValue = this.getDefaultValue(),
            delta;

        // Ensure valid input
        this.checkIndex(index);

        // Calculate change in value
        oldValue = this.getValue(index);

        if (oldValue === null) {
            oldValue = this.getDefaultValue();
        }
        delta = newValue - oldValue;

        // Store new value and update fenwick tree
        delete this.values[index];
        this.updateTreeFrequency(index, delta);
    },

    /**
     * Sets the value of the item at the given index.
     *
     * @param {number}  index       The index
     * @param {number}  newValue  The new value
     * @public
     */
    setValue: function(index, newValue) {
        var oldValue,
            delta;

        // Ensure valid input
        this.checkIndex(index);
        this.checkValue(newValue);

        // Calculate change in value
        oldValue = this.getValue(index);

        if (oldValue === null) {
            oldValue = this.getDefaultValue();
        }
        delta = newValue - oldValue;

        // Store new value and update fenwick tree
        this.values[index] = newValue;
        this.updateTreeFrequency(index, delta);
    },

    /**
     * Returns the sum of all values up to and including the given index.
     *
     * @param  {number}  index  The index
     * @return {number}  The sum
     * @public
     */
    getSum: function(index) {
        var sum = 0;

        this.checkIndex(index);

        while (index > 0) {
            sum   += this.getTreeFrequency(index);
            index -= (index & -index);
        }

        return sum;
    },

    /**
     * Returns the highest index where the sum of all values up to and including
     * this index, is smaller than the given sum.
     *
     * @param  {number}  sum  The sum
     * @return {number}  The index
     * @public
     */
    lowerBound: function(sum) {
        return this.find(sum, function(sumM, sumT) {
            return sumM < sumT;
        });
    },

    /**
     * Returns the highest index where the sum of all values up to and including
     * this index, is smaller than or equal to the given sum.
     *
     * @param  {number}  sum  The sum
     * @return {number}  The index
     * @public
     */
    upperBound: function(sum) {
        return this.find(sum, function(sumM, sumT) {
            return sumM <= sumT;
        });
    }

});
